import { useState, useCallback } from "react";
import { motion } from "framer-motion";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Menu } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";

const HONEYBOOK_URL = "https://app.hellobonsai.com/s/n-santiago-media/strategycall";

const navLinks = [
  { label: "Dental Marketing", href: "/healthcare-marketing", internal: true },
  { label: "Services", href: "/services", internal: true },
  { label: "Insights", href: "/insights", internal: true },
  { label: "Careers", href: "/healthcare-marketing-careers", internal: true },
  { label: "Service Areas", href: "/locations", internal: true },
  { label: "Gallery", href: "https://nsantiago.pic-time.com/client", external: true },
];

const serviceLinks = [
  { label: "Dental Marketing Agency in Florida", href: "/dental-marketing-agency-florida" },
  { label: "Dental Marketing — Port St. Lucie", href: "/dental-marketing-port-st-lucie" },
  { label: "Dental Marketing — Stuart, FL", href: "/dental-marketing-stuart-fl" },
  { label: "Google Business Profile for Dentists", href: "/google-business-profile-for-dentists" },
  { label: "Dental Video Marketing", href: "/dental-video-marketing" },
  { label: "Dental Patient Conversion System", href: "/dental-patient-conversion-system" },
  { label: "Local SEO for Dentists", href: "/seo-treasure-coast" },
  { label: "Select Local Business Marketing", href: "/services" },
];


const Navbar = () => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const handleHashClick = useCallback((e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    const hash = href.replace("/#", "");
    if (location.pathname === "/") {
      e.preventDefault();
      const el = document.getElementById(hash);
      if (el) el.scrollIntoView({ behavior: "smooth" });
    }
    // If not on homepage, the <a href="/#hash"> will navigate to / and the browser handles the hash scroll
  }, [location.pathname]);

  const renderNavLink = (link: typeof navLinks[0], className: string, onClick?: () => void) => {
    if (link.external) {
      return (
        <a key={link.label} href={link.href} target="_blank" rel="noopener noreferrer" onClick={onClick} className={className}>
          {link.label}
        </a>
      );
    }
    if (link.internal) {
      return (
        <Link key={link.label} to={link.href} onClick={onClick} className={className}>
          {link.label}
        </Link>
      );
    }
    // Hash link — use <a> so it navigates to /#hash from any page
    return (
      <a key={link.label} href={link.href} onClick={(e) => { handleHashClick(e, link.href); onClick?.(); }} className={className}>
        {link.label}
      </a>
    );
  };

  return (
    <motion.nav
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="fixed top-0 left-0 right-0 z-50 bg-navy/95 backdrop-blur-md border-b border-border"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-16">
        <Link to="/" className="flex items-center gap-3">
          <picture>
            <source srcSet="/images/logo-white-sm.webp" type="image/webp" />
            <img src="/images/logo-white-sm.png" alt="N. Santiago Media" width={123} height={48} className="h-12 w-auto" decoding="async" />
          </picture>
        </Link>

        <div className="hidden md:flex items-center gap-8 text-sm">
          {navLinks.map((link) => renderNavLink(link, "text-white/70 hover:text-white transition-colors"))}
          <a href={HONEYBOOK_URL} target="_blank" rel="noopener noreferrer">
            <Button variant="hero" size="sm">Book a Call</Button>
          </a>
        </div>

        <button onClick={() => setOpen(true)} className="md:hidden text-white p-2" aria-label="Open menu">
          <Menu className="h-6 w-6" />
        </button>

        <Sheet open={open} onOpenChange={setOpen}>
          <SheetContent side="right" className="bg-navy border-border w-72">
            <SheetTitle className="sr-only">Navigation Menu</SheetTitle>
            <nav className="flex flex-col gap-6 mt-8">
              {navLinks.map((link) => renderNavLink(link, "text-white/80 hover:text-white text-lg transition-colors", () => setOpen(false)))}

              <div className="border-t border-white/10 pt-4">
                <p className="text-primary text-xs font-semibold uppercase tracking-widest mb-3">Services</p>
                {serviceLinks.map((link) => (
                  <Link key={link.label} to={link.href} onClick={() => setOpen(false)} className="block text-white/70 hover:text-white text-sm py-1.5 transition-colors">
                    {link.label}
                  </Link>
                ))}
              </div>

              <a href={HONEYBOOK_URL} target="_blank" rel="noopener noreferrer" onClick={() => setOpen(false)}>
                <Button variant="hero" size="sm" className="w-full mt-2">Book a Call</Button>
              </a>
            </nav>
          </SheetContent>
        </Sheet>
      </div>
    </motion.nav>
  );
};

export default Navbar;
